import React, { useState, useCallback, useEffect } from 'react';
import { useDarkMode } from '../../../../context/DarkModeContext';
import { InternshipResult, ResultDocument } from '../../../../types/internship';
import { CareerFocusWithMetrics } from '../../../../hooks/useCareerFocus';
import { useAuth } from '../../../../context/AuthContext';
import { useToast } from '../../../../context/ToastContext';
import { supabase } from '../../../../utils/supabase';
import { ModernFileService } from '../../../../utils/modernFileService';
import { 
  DocumentTextIcon, 
  CloudArrowUpIcon,
  XMarkIcon,
  CheckCircleIcon
} from '@heroicons/react/24/outline';

interface DocumentUploadProps {
  data: Partial<InternshipResult>;
  onUpdate: (updates: Partial<InternshipResult>) => void;
  focus: CareerFocusWithMetrics;
}

interface UploadedDoc {
  fileId: string;
  fileName: string;
  documentType: string;
  uploadedAt: Date;
}

const DocumentUpload: React.FC<DocumentUploadProps> = ({
  data,
  onUpdate,
  focus
}) => {
  const { darkMode } = useDarkMode();
  const { user } = useAuth();
  const { showToast } = useToast();
  const fileService = ModernFileService.getInstance();
  
  const [uploadedDocs, setUploadedDocs] = useState<UploadedDoc[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  
  const bgColor = darkMode ? 'bg-gray-700' : 'bg-gray-50';
  const textColor = darkMode ? 'text-gray-100' : 'text-gray-900';
  const borderColor = darkMode ? 'border-gray-600' : 'border-gray-300';
  const mutedTextColor = darkMode ? 'text-gray-400' : 'text-gray-600';
  const dragBg = darkMode ? 'bg-gray-600' : 'bg-blue-50';
  
  const documentTypes = [
    { value: 'offer_letter', label: 'Offer Letter' },
    { value: 'contract', label: 'Contract' },
    { value: 'benefits_summary', label: 'Benefits Summary' },
    { value: 'feedback_email', label: 'Feedback Email' },
    { value: 'rejection_letter', label: 'Rejection Letter' },
    { value: 'other', label: 'Other Document' }
  ];
  
  const handleDrag = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  }, []);
  
  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFiles(e.dataTransfer.files);
    }
  }, []);
  
  const handleFiles = async (files: FileList) => {
    if (!user) return;
    
    const file = files[0]; // Handle one file at a time for now
    if (!file) return;
    
    // Validate file
    const maxSize = 10 * 1024 * 1024; // 10MB
    if (file.size > maxSize) {
      showToast('File too large. Maximum size is 10MB.', 'error');
      return;
    }
    
    const allowedTypes = [
      'application/pdf',
      'image/png',
      'image/jpeg',
      'image/jpg',
      'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
    ];
    
    if (!allowedTypes.includes(file.type)) {
      showToast('Invalid file type. Please upload PDF, images, or Word documents.', 'error');
      return;
    }
    
    setIsUploading(true);
    
    try {
      // Determine document type based on outcome
      let suggestedType = 'other';
      if (data.outcomeType === 'offer_received' && file.name.toLowerCase().includes('offer')) {
        suggestedType = 'offer_letter';
      } else if (data.outcomeType === 'no_offer') {
        suggestedType = 'feedback_email';
      }
      
      // Upload file using ModernFileService
      const uploadedFile = await fileService.uploadFile(
        file,
        'result-documents',
        {
          type: 'internship_result_document',
          document_type: suggestedType,
          result_id: data.id,
          context: `${suggestedType.replace('_', ' ')} for ${focus.company} internship`
        }
      );
      
      if (uploadedFile) {
        // If we have a result ID, create the result_document record
        if (data.id) {
          const { error } = await supabase
            .from('result_documents')
            .insert({
              result_id: data.id,
              user_id: user.id,
              file_id: uploadedFile.id,
              document_type: suggestedType
            });
          
          if (error) {
            console.error('Error linking document:', error);
            showToast('Document uploaded but linking failed', 'warning');
          }
        }
        
        setUploadedDocs(prev => [...prev, {
          fileId: uploadedFile.id,
          fileName: file.name,
          documentType: suggestedType,
          uploadedAt: new Date()
        }]);
        
        showToast('Document uploaded successfully!', 'success');
      }
    } catch (error) {
      console.error('Upload error:', error);
      showToast('Failed to upload document', 'error');
    } finally {
      setIsUploading(false);
    }
  };
  
  const handleFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      handleFiles(e.target.files);
    }
  };
  
  const removeDocument = (fileId: string) => {
    setUploadedDocs(prev => prev.filter(doc => doc.fileId !== fileId));
    // Note: We're not deleting the file from storage, just removing from this session
  };
  
  // Update parent component whenever uploaded documents change
  useEffect(() => {
    const newDocuments = uploadedDocs.map(doc => ({
      id: doc.fileId,
      fileId: doc.fileId,
      documentName: doc.fileName,
      documentType: doc.documentType as any,
      uploadedAt: doc.uploadedAt
    }));
    
    // Only update if documents have actually changed
    const existingDocs = data.documents || [];
    const hasChanges = 
      newDocuments.length !== existingDocs.length ||
      JSON.stringify(newDocuments) !== JSON.stringify(existingDocs);
    
    if (hasChanges && uploadedDocs.length > 0) {
      onUpdate({
        documents: newDocuments
      });
    }
  }, [uploadedDocs]);
  
  return (
    <div className="space-y-6">
      <div>
        <h2 className={`text-xl font-semibold mb-2 ${textColor}`}>
          Upload Supporting Documents
        </h2>
        <p className={mutedTextColor}>
          Attach any relevant documents (offer letters, feedback emails, etc.)
        </p>
      </div>
      
      {/* Upload Area */}
      <div
        className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
          dragActive ? `${dragBg} border-blue-500` : `${borderColor} ${bgColor}`
        }`}
        onDragEnter={handleDrag}
        onDragLeave={handleDrag}
        onDragOver={handleDrag}
        onDrop={handleDrop}
      >
        <input
          type="file"
          id="file-upload"
          className="hidden"
          onChange={handleFileInput}
          accept=".pdf,.png,.jpg,.jpeg,.doc,.docx"
          disabled={isUploading}
        />
        
        <CloudArrowUpIcon className={`w-12 h-12 mx-auto mb-4 ${mutedTextColor}`} />
        
        <label
          htmlFor="file-upload"
          className={`cursor-pointer inline-flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-colors ${
            darkMode
              ? 'bg-blue-600 hover:bg-blue-700 text-white'
              : 'bg-blue-500 hover:bg-blue-600 text-white'
          } ${isUploading ? 'opacity-50 cursor-not-allowed' : ''}`}
        >
          {isUploading ? 'Uploading...' : 'Choose File'}
        </label>
        
        <p className={`mt-3 text-sm ${mutedTextColor}`}>
          or drag and drop a file here
        </p>
        <p className={`text-xs ${mutedTextColor} mt-1`}>
          PDF, PNG, JPG, DOC up to 10MB
        </p>
      </div>
      
      {/* Uploaded Documents */}
      {uploadedDocs.length > 0 && (
        <div className="space-y-2">
          <h3 className={`text-sm font-medium ${textColor}`}>Uploaded Documents</h3>
          {uploadedDocs.map(doc => (
            <div
              key={doc.fileId}
              className={`flex items-center justify-between p-3 rounded-lg ${bgColor}`}
            >
              <div className="flex items-center gap-3">
                <DocumentTextIcon className="w-5 h-5" />
                <div>
                  <p className={`text-sm font-medium ${textColor}`}>{doc.fileName}</p>
                  <p className={`text-xs ${mutedTextColor}`}>
                    {documentTypes.find(t => t.value === doc.documentType)?.label || 'Document'}
                  </p>
                </div>
              </div>
              <button
                onClick={() => removeDocument(doc.fileId)}
                className={`p-1 rounded hover:bg-gray-600`}
              >
                <XMarkIcon className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>
      )}
      
      {/* Info Box */}
      <div className={`p-4 rounded-lg ${bgColor} flex items-start gap-3`}>
        <CheckCircleIcon className="w-5 h-5 text-green-500 mt-0.5" />
        <div>
          <p className={`text-sm ${textColor}`}>
            All documents are stored securely and remain private to you.
          </p>
          <p className={`text-sm ${mutedTextColor} mt-1`}>
            You can view and manage all uploaded files in the Files section.
          </p>
        </div>
      </div>
    </div>
  );
};

export default DocumentUpload;